/** @jsxImportSource @emotion/react */
import { Button } from 'react-bootstrap5';
import JSONSchemaForm from '@rjsf/bootstrap-4';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef, useState } from 'react';
import { Col } from 'react-bootstrap';
import { types as coreTypes } from '@sitebuilder/core';
import {
    widgetUtils,
    hooks as commonHooks,
    themes as commonThemes,
    utils as commonUtils,
} from '@sitebuilder/common';

import Widgets from '../../components/CustomSchemaWidgets';
import Fields from '../../components/CustomSchemaFields';
import { actionCreators as themeActionCreators } from '../../reducers/themes';
import { StateShape as ReduxStateShape } from '../../reducers';
// import { Theme, Typography, TypoGraphyValues } from '../../reducers/types';
// import { getFilteredTheme, isJSON } from '../../WidgetLib/utils';
import { SpinnerIcon } from '../../components/BaseComponents';
import RightSideBar from '../../components/Layouts/RightSideBar';
import { iconSaveSvg } from '../../assets/images';
import ThemeEditorSchema from './ThemeEditorSchema';
import ThemeEditorContainer from './ThemeEditorContainer';
// import { defaultTypography } from './defaultTypography';
// import { defaultFonts } from './defaultFonts';
// import { defaultColors } from './defaultColors';
// import { defaultPalettes } from './defaultPalettes';
import '../../scss/index.scss';
import themeEditorStyles from './ThemeEditor.styles';
// import '../../scss/user/index.scss';

interface ThemeEditorProps {
    themeId: string;
}
export const formatTypographyObject = (typography: coreTypes.Typography) =>
    Object.entries(typography).map(([key, value]: [string, coreTypes.TypoGraphyValues]) => ({
        key,
        value,
    }));

export const formatFontObject = (typography: coreTypes.Typography) =>
    Object.entries(typography).map(([key, value]: [string, coreTypes.TypoGraphyValues]) => ({
        key,
        value,
    }));

export const formatPalettesObject: any = (palettes: any) => {
    return palettes
        ? Object.keys(palettes).map((key) => {
              return {
                  paletteName: key,
                  key: key,
                  colors: palettes?.[key]
                      ? Object.keys(palettes?.[key]).map((k) => {
                            return {
                                key: k,
                                value: JSON.stringify(palettes?.[key]?.[k]),
                            };
                        })
                      : [],
              };
          })
        : [];
};

const ThemeEditor = (props: ThemeEditorProps) => {
    const { themeId } = props;
    const dispatch = useDispatch();
    const [formData, setFormData] = useState<any>();
    const formRef = useRef<any>(null);

    const i18n = commonHooks.useLocalization();

    useEffect(() => {
        if (themeId) {
            dispatch(themeActionCreators.loadTheme(themeId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [themeId]);

    const { theme, themeSaveLoading, themeSaveSucess } = useSelector((state: ReduxStateShape) => ({
        ...state.themes,
    }));

    useEffect(() => {
        if (themeId && !themeSaveLoading && themeSaveSucess) {
            dispatch(themeActionCreators.loadTheme(themeId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [themeSaveSucess, themeSaveLoading]);

    const [newTheme, setNewTheme] = useState<coreTypes.Theme | null>(theme);

    useEffect(() => {
        if (theme) {
            const filteredTheme: any = widgetUtils.getFilteredTheme(theme);
            const data: any = {
                defaultColors: Object.keys(commonThemes.defaultColors)?.map((k: string) => ({
                    key: k,
                    value: JSON.stringify(commonThemes.defaultColors?.[k]),
                })),
            };
            let typography: any = [];

            if (filteredTheme) {
                Object.keys(filteredTheme)?.forEach((key: string) => {
                    data[key] = Object.keys(filteredTheme[key])?.map((k: string) => ({
                        key: k,
                        value: JSON.stringify(filteredTheme?.[key]?.[k]),
                    }));
                });

                if (theme?.defaultColors) {
                    const colors: any = { ...commonThemes.defaultColors, ...theme.defaultColors };
                    data.defaultColors = Object.keys(colors)?.map((k: string) => ({
                        key: k,
                        value: JSON.stringify(colors?.[k]),
                    }));
                }

                typography = [
                    ...formatTypographyObject(
                        Object.assign(
                            {},
                            commonThemes.defaultTypography,
                            filteredTheme.typography || {}
                        )
                    ),
                ];

                data.userTypography = [
                    ...formatTypographyObject(filteredTheme.userTypography || {}),
                ];

                data.typography = typography;
                data.defaultFonts = filteredTheme?.defaultFonts
                    ? filteredTheme?.defaultFonts
                    : commonThemes.defaultFonts;

                data.userFonts = filteredTheme?.userFonts ? filteredTheme.userFonts : [];

                Object.keys(commonThemes.componentThemes).forEach((key: any) => {
                    data[key] = filteredTheme?.componentThemes?.[key]
                        ? {
                              ...commonThemes.componentThemes[key],
                              ...filteredTheme.componentThemes[key],
                          }
                        : commonThemes.componentThemes[key];
                });

                data.defaultPalettes = formatPalettesObject(
                    Object.assign(
                        {},
                        commonThemes.defaultPalettes,
                        filteredTheme.defaultPalettes || {}
                    )
                );

                data.palettes = formatPalettesObject(filteredTheme.palettes);

                delete data.componentThemes;

                setFormData(data);
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [theme]);

    const onClickSubmit = (formData: any) => {
        const updatedTheme: any = {};
        Object.keys(formData)?.forEach((k: string) => {
            if (['defaultFonts', 'userFonts'].includes(k)) {
                updatedTheme[k] = formData[k];
            } else if (Object.keys(commonThemes.componentThemes).includes(k)) {
                if (!updatedTheme.componentThemes) {
                    updatedTheme.componentThemes = {};
                }

                updatedTheme.componentThemes[k] = formData[k];
            } else if (k === 'palettes' || k === 'defaultPalettes') {
                updatedTheme[k] = {
                    ...updatedTheme[k],
                    ...formData[k].reduce((obj: any, item: any) => {
                        obj[item.paletteName] = {};
                        item.colors?.forEach((color: any) => {
                            obj[item.paletteName][color.key] = color.value
                                ? commonUtils.isJSON(color.value)
                                    ? JSON.parse(color.value)
                                    : color.value
                                : {};
                        });

                        return obj;
                    }, {}),
                };
            } else {
                formData?.[k]?.forEach((obj: any) => {
                    updatedTheme[k] = {
                        ...updatedTheme[k],
                        [obj.key]: ['colors', 'defaultColors'].includes(k)
                            ? commonUtils.isJSON(obj.value)
                                ? JSON.parse(obj.value)
                                : obj.value
                            : obj.value,
                    };
                });
            }
        });
        setNewTheme(updatedTheme);
    };

    const onClickSave = () => {
        if (newTheme) dispatch(themeActionCreators.saveNewTheme(themeId, newTheme));
    };

    const transformErrors = (errors: any) => {
        return errors.map((error: any) => {
            if (error.name === 'pattern' && error.params?.pattern === '^[A-Za-z]+$') {
                error.message = 'Only characters are allowed';
            }

            if (error.name === 'pattern' && error.params?.pattern === '^[0-9]+$') {
                error.message = 'Only digits are allowed';
            }

            return error;
        });
    };

    return (
        <div className="app" css={themeEditorStyles}>
            <div className="pane-header">
                <div className="toolbar">
                    <Button className={'toolbar-button'} type="button" onClick={onClickSave}>
                        {themeSaveLoading ? (
                            <SpinnerIcon />
                        ) : (
                            <img className="save-icon" src={iconSaveSvg} alt="icom-save-svg" />
                        )}
                        <span>{i18n.t('save')}</span>
                    </Button>
                </div>
                <Col>
                    <RightSideBar>
                        <div className="right-sidebar-tool">
                            <div className="container">
                                <div className="color-picker-sidebar"></div>
                                <div className="editor">
                                    {formData && (
                                        <JSONSchemaForm
                                            ref={formRef}
                                            schema={ThemeEditorSchema.schema}
                                            uiSchema={ThemeEditorSchema.uiSchema}
                                            widgets={Widgets}
                                            fields={Fields}
                                            formData={formData}
                                            onChange={({ formData }: any) => {
                                                setFormData(formData);
                                                setTimeout(
                                                    () =>
                                                        onClickSubmit(
                                                            formRef?.current?.state?.formData
                                                        ),
                                                    300
                                                );
                                            }}
                                            transformErrors={transformErrors}
                                            onError={(e: any) => console.log(e)}
                                            id="Schemaform"
                                        >
                                            <></>
                                        </JSONSchemaForm>
                                    )}
                                </div>
                            </div>
                        </div>
                    </RightSideBar>
                </Col>
            </div>
            <ThemeEditorContainer theme={{ ...theme, ...newTheme }} themeId={themeId} />
        </div>
    );
};

export default ThemeEditor;
